// supabase/functions/google-api-service/utils/flatten-data.ts
export function flattenObject(obj: unknown, prefix = '', result: Record<string, unknown> = {}): Record<string, unknown> {
  if (obj === null || obj === undefined) {
    return result;
  }
  if (Array.isArray(obj)) {
    (obj as unknown[]).forEach((item, index)=>{
      flattenObject(item, `${prefix}.${index}`, result);
    });
  } else if (typeof obj === 'object' && !(obj instanceof Date)) {
    const record = obj as Record<string, unknown>;
    Object.keys(record).forEach((key) => {
      const newPrefix = prefix ? `${prefix}.${key}` : key;
      flattenObject(record[key], newPrefix, result);
    });
  } else {
    result[prefix] = obj;
  }
  return result;
}
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (value instanceof Date) {
    try {
      return value.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch  {
      return String(value);
    }
  }
  if (typeof value === 'number') {
    return value.toLocaleString('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  // Everything left is a primitive — `flattenObject` recurses into objects, so
  // nothing structured should reach here; if it does we keep JS's default
  // stringification rather than throw. Both assertions are type-only: `String()`
  // still calls the value's own `toString`, so e.g. arrays stringify as before
  // ("1,2", not "[object Array]").
  return typeof value === 'object'
    ? String(value as { toString(): string })
    : String(value as string | number | boolean | bigint | symbol);
}
export function formatFlattenedData(flattened: Record<string, unknown>): Record<string, string> {
  const formatted: Record<string, string> = {};
  Object.entries(flattened).forEach(([key, value]) => {
    formatted[key] = formatValue(value);
  });
  return formatted;
}

/**
 * Convert snake_case to camelCase
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/**
 * Convert object keys from snake_case to camelCase recursively
 */
/**
 * Convert camelCase to snake_case
 */
function toSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/**
 * Convert object keys from camelCase to snake_case recursively
 */
// `T` is an unchecked view of the converted result: the runtime shape depends on
// the caller's row, which only the caller knows. Defaults to `unknown` so an
// un-annotated call forces an explicit cast at the use site.
export function keysToSnakeCase<T = unknown>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return (obj as unknown[]).map(item => keysToSnakeCase(item)) as T;
  }

  if (typeof obj === 'object' && !(obj instanceof Date)) {
    const source = obj as Record<string, unknown>;
    const snakeObj: Record<string, unknown> = {};
    Object.keys(source).forEach(key => {
      const snakeKey = toSnakeCase(key);
      snakeObj[snakeKey] = keysToSnakeCase(source[key]);
    });
    return snakeObj as T;
  }

  return obj as T;
}

export function keysToCamelCase<T = unknown>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return (obj as unknown[]).map(item => keysToCamelCase(item)) as T;
  }

  if (typeof obj === 'object' && !(obj instanceof Date)) {
    const source = obj as Record<string, unknown>;
    const camelObj: Record<string, unknown> = {};
    Object.keys(source).forEach(key => {
      const camelKey = toCamelCase(key);
      camelObj[camelKey] = keysToCamelCase(source[key]);
    });
    return camelObj as T;
  }

  return obj as T;
}
